import { findRegisteredIntegrationConfig } from '@main/integrations/registry/configs';
import { isConfigurableSchema } from '@main/shared/utils';
import { Namespace, TFunction } from 'i18next';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import z from 'zod/v4';

import type { FormModel } from '../schema-form';
import {
  createSchemaFieldModel,
  hasDirtyValues,
  makeIntegrationZodResolver,
  nullToUndefined,
  SchemaForm,
} from '../schema-form';
import { configForwardRef, ConfigRef } from './types';

type ConfigGenericFormProps = {
  t: TFunction<Namespace>;
  tKey: string;
  schema: z.ZodObject;
  defaultValues?: Record<string, unknown>;
  isDisabled?: boolean;
  onDirtyChange?: (isDirty: boolean) => void;
};

export const ConfigGeneric = configForwardRef<Record<string, unknown>>(
  ({ providerName, serviceName, handlerName, evidenceConfig, isDisabled, onDirtyChange }, ref) => {
    const { t } = useTranslation('integrations');
    const configFormRef = useRef<ConfigRef<Record<string, unknown>>>(null);
    const tPrefix = `integrations.${providerName}.services.${serviceName}.handlers.${handlerName}`;

    const config = useMemo(() => {
      if (!serviceName || !handlerName) return;
      const integrationConfig = findRegisteredIntegrationConfig({
        providerName,
        serviceName,
        handlerName,
      });
      if (!integrationConfig) return;
      const description = t(`${tPrefix}.description`, { defaultValue: '' });
      return {
        description,
        schema: integrationConfig.configSchema,
      };
    }, [handlerName, providerName, serviceName, t, tPrefix]);

    useImperativeHandle(ref, () => ({
      submit: async () => {
        if (!serviceName || !handlerName) return;
        return configFormRef.current?.submit();
      },
      reset: () => {
        configFormRef.current?.reset?.();
      },
    }));

    if (!config || !isConfigurableSchema(config.schema)) {
      return null;
    }

    return (
      <ConfigGenericForm
        key={tPrefix}
        t={t}
        ref={configFormRef}
        tKey={`${tPrefix}.config`}
        schema={config.schema}
        defaultValues={evidenceConfig}
        isDisabled={isDisabled}
        onDirtyChange={onDirtyChange}
      />
    );
  },
);

const ConfigGenericForm = forwardRef<ConfigRef<Record<string, unknown>>, ConfigGenericFormProps>(
  ({ t, tKey, schema, defaultValues, isDisabled = false, onDirtyChange }, ref) => {
    const model = useMemo<FormModel<Record<string, unknown>, never, Record<string, unknown>>>(
      () => ({
        inputSchema: schema,
        defaultValues: defaultValues ? schema.parse(defaultValues) : {},
        fields: createSchemaFieldModel<Record<string, unknown>>({
          schema,
          namePrefix: '',
          pathPrefix: [],
          t,
          tKey,
        }),
        serialize: ({ values }) => values,
      }),
      [defaultValues, schema, t, tKey],
    );
    const form = useForm<Record<string, unknown>>({
      defaultValues: model.defaultValues,
      resolver: makeIntegrationZodResolver(model.inputSchema, t),
    });

    useImperativeHandle(
      ref,
      () => ({
        submit: async () => {
          const isValid = await form.trigger();
          if (!isValid) return;
          return model.serialize({
            values: nullToUndefined(form.getValues()),
            dirtyFields: form.formState.dirtyFields,
          });
        },
        reset: () => {
          form.reset(form.getValues());
        },
      }),
      [form, model],
    );

    const hasDirtyFields = hasDirtyValues(form.formState.dirtyFields);

    useEffect(() => {
      onDirtyChange?.(hasDirtyFields);
    }, [hasDirtyFields, onDirtyChange]);

    return <SchemaForm model={model} form={form} isDisabled={isDisabled} />;
  },
);
